import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchInput, { Filters } from './SearchInput/SearchInput';
import SearchTableView from './SearchTable/SearchTableView';
import AppButton from '../../components/AppButton/AppButton';
import styles from '../TargetGroupView/TargetGroupTable/TargetGroupTableFooter/TargetGroupTableFooter.module.css';
import { ISearchRequest, useSearchRequestQuery } from '../../store/api/search';
import { AuthenticatedUserProps } from '../../hoc/WithAuthLayout';
import { SearchViewHeader, SearchViewWrapper } from './Styles';
import { PeopleIcon } from 'evergreen-ui';
import { getSearchPaginationRange } from './utils';

const useQuery = () => new URLSearchParams(useLocation().search);

interface SearchViewProps extends AuthenticatedUserProps {}

const SearchView: React.FC<SearchViewProps> = ({
    userRoles,
    hasMultipleCompanies,
}) => {
    const queryParams = useQuery();
    const navigate = useNavigate();
    const location = useLocation();

    const companyId = queryParams.get('company_id') ?? '';
    const groupId = queryParams.get('group_id') ?? '';
    const query = queryParams.get('query') ?? '';
    const sort = queryParams.get('sort') ?? '_score:desc';

    const invitedFrom = queryParams.get('invited_from') ?? undefined;
    const invitedTo = queryParams.get('invited_to') ?? undefined;
    const reportFrom = queryParams.get('report_generated_from') ?? undefined;
    const reportTo = queryParams.get('report_generated_to') ?? undefined;

    const filters: Filters = useMemo(() => {
        return {
            invitedAt: {
                from: invitedFrom,
                to: invitedTo,
            },
            reportGeneratedAt: {
                from: reportFrom,
                to: reportTo,
            },
        };
    }, [invitedFrom, invitedTo, reportFrom, reportTo]);

    const currentPage = parseInt(queryParams.get('page') ?? '1', 10) - 1;
    const rowsPerPage = parseInt(queryParams.get('size') ?? '50', 10);

    const buildSearchRequest = useCallback((): ISearchRequest => {
        const requestFilters: ISearchRequest['filters'] = {};

        if (filters.invitedAt?.from || filters.invitedAt?.to) {
            requestFilters.invitedAt = {};
            if (filters.invitedAt.from)
                requestFilters.invitedAt.from = filters.invitedAt.from;
            if (filters.invitedAt.to)
                requestFilters.invitedAt.to = filters.invitedAt.to;
        }

        if (filters.reportGeneratedAt?.from || filters.reportGeneratedAt?.to) {
            requestFilters.reportGeneratedAt = {};
            if (filters.reportGeneratedAt.from)
                requestFilters.reportGeneratedAt.from =
                    filters.reportGeneratedAt.from;
            if (filters.reportGeneratedAt.to)
                requestFilters.reportGeneratedAt.to =
                    filters.reportGeneratedAt.to;
        }

        const [sortField, sortDirection] = sort.split(':');
        const sortValue =
            sortField && sortDirection
                ? [{ [sortField]: sortDirection }]
                : undefined;

        return {
            query: query || undefined,
            filters: Object.keys(requestFilters).length
                ? requestFilters
                : undefined,
            sort: sortValue,
            pagination: {
                from: currentPage * rowsPerPage,
                size: rowsPerPage,
            },
        };
    }, [query, filters, sort, currentPage, rowsPerPage]);

    const { data, isLoading, refetch } = useSearchRequestQuery({
        searchRequestData: buildSearchRequest(),
        companyId,
        groupId,
    });

    const isSuperAdmin = userRoles.includes('super-admin');

    const paginationRange = getSearchPaginationRange(
        currentPage + 1,
        Math.ceil((data?.total || 0) / rowsPerPage),
        2,
    );

    const setPageToFirst = () => {
        const params = new URLSearchParams(location.search);
        params.set('page', '1');
        navigate(`/search?${params.toString()}`);
    };

    return (
        <SearchViewWrapper>
            <SearchViewHeader>
                <div className="header-wrapper">
                    <div className="header-info">
                        <div className="header-info-name">
                            <h2>Search results</h2>
                        </div>
                        <div className="results-number">
                            <div className="icon">
                                <PeopleIcon size={13} />
                            </div>
                            {data && (
                                <span className="text">
                                    {data.total === 1
                                        ? `1 candidate`
                                        : `${data.total} candidates`}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <SearchInput
                    initialQuery={query}
                    initialFilters={filters}
                    initialSort={sort}
                    companyId={companyId}
                    groupId={groupId}
                />
            </SearchViewHeader>

            <SearchTableView
                isSuperAdmin={isSuperAdmin}
                isSearchLoading={isLoading}
                participantHasMultipleCompanies={hasMultipleCompanies}
                results={data?.items ?? []}
                currentPage={currentPage}
                refetchSearch={refetch}
                setPageToFirst={setPageToFirst}
            />

            {data && data.total > rowsPerPage && (
                <div className={styles.pages_wrapper}>
                    {paginationRange.map((page, index) =>
                        typeof page === 'string' ? (
                            <span key={index} className={styles.pages_ellipsis}>
                                {page}
                            </span>
                        ) : (
                            <AppButton
                                key={index}
                                color={
                                    page === currentPage + 1
                                        ? 'tealBlue'
                                        : 'gray'
                                }
                                onClick={() => {
                                    const params = new URLSearchParams(
                                        location.search,
                                    );
                                    params.set('page', page.toString());
                                    navigate(`/search?${params.toString()}`);
                                }}>
                                {page}
                            </AppButton>
                        ),
                    )}
                </div>
            )}
        </SearchViewWrapper>
    );
};

export default SearchView;
